// The pre-launch marker — a "BETA VERSION" chip, top-right of every page.
// Until 2026-09-15 a launch card (date, event, venue) sat under the chip;
// it was removed on request, on every page, and the chip stays.
//
// Rendered once in App.js OUTSIDE <Routes> rather than inside SiteHeader,
// because the header only wraps the public marketing routes (PublicLayout).
// /login and /dashboard/* sit outside it, and a beta notice that disappears
// exactly where someone is using the product is the wrong half to cover.
//
// It sits BELOW the header rather than in the corner proper: .nav is
// position:fixed, 68px tall, and its right end already carries the auth CTA
// cluster ("Talk to an advisor" / "Go to Dashboard"). Pinning to top:0 would
// land this on top of those buttons. At 78px down and ~23px tall it ends at
// ~101px, above where the marketing heroes start their content (108px), so
// none of them clears it any more — the clearance rules that the taller
// launch card needed (borrower-flow.css) went with the card.
//
// NOT OBSCURING THE PAGE
// Being position:fixed, this rides down the page and sits over whatever
// scrolls under it. Two things keep that from costing anything:
//   1. a translucent surface + a backdrop blur, so content stays visible
//      THROUGH the chip rather than being hidden by it;
//   2. it dims once you scroll away from the top, and returns to full
//      strength when you come back up. The notice matters most on arrival;
//      after that it only needs to stay noticeable.
// The dimmed value is 0.62, not lower: opacity MULTIPLIES with the chip's own
// background alpha, so a first pass at 0.28 landed the fill near 0.18 and the
// badge disappeared outright while scrolling. Anything below ~0.5 reads as gone.
// pointerEvents is 'none' throughout, so it can never intercept a click on
// whatever sits beneath it either.
//
// A previous revision added a viewport frame and a full-width bottom bar as
// well. Dropped as too loud — it read as a system warning, not a launch note.
//
// aria-hidden is deliberately NOT set: this is real information about the
// state of the product, and a screen-reader user has the same interest in it.
//
// THE DASHBOARD SAYS IT IN ITS OWN TOP BAR (2026-10-31). Dimmed or not, the chip
// sat over the right end of whatever scrolled under it — Balance Transfer's
// banner, for one. That bar has room for the notice, so this one steps aside.

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/** Scrolled past this many px → the badge recedes. */
const FADE_AFTER = 120;

/** The dashboard's pages carry the notice in their top bar (Dashboard.js). */
export const carriesOwnNotice = (pathname = '') => /^\/dashboard(\/|$)/.test(pathname);

const WRAP = (faded) => ({
  position: 'fixed',
  top: 78,                 // 68px nav + 10px breathing room
  right: 16,
  zIndex: 90,              // under .nav (100) — the bar stays clickable
  pointerEvents: 'none',   // never eat a click on the content underneath
  opacity: faded ? 0.62 : 0.96,
  transition: 'opacity .25s ease',
  willChange: 'opacity',
});

const CHIP = {
  display: 'inline-block',
  background: 'rgba(245,158,11,.92)',
  color: '#fff',
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: '.10em',
  textTransform: 'uppercase',
  padding: '4px 10px',
  borderRadius: 999,
  lineHeight: 1.4,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
};

export default function BetaBadge() {
  const { pathname } = useLocation();
  const [faded, setFaded] = useState(false);

  useEffect(() => {
    // Passive: this must never delay a scroll. Reading scrollY in the handler
    // is a cheap property read, and the state only flips on the threshold
    // crossing, so React re-renders twice per page-length scroll, not per event.
    const onScroll = () => setFaded(window.scrollY > FADE_AFTER);
    onScroll();                                    // honour a restored scroll position
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (carriesOwnNotice(pathname)) return null;

  return (
    <div className="beta-badge" style={WRAP(faded)} data-testid="beta-badge">
      <span style={CHIP}>Beta version</span>
    </div>
  );
}
